import { ApiTableFilter } from '../../../shared/models/table-filter.model';

export const GS1_ATTRIBUTE_TYPE = 'GS1';

export type AttributeFieldType =
  | 'SELECT'
  | 'TEXT'
  | 'DATE'
  | 'CHECKBOX'
  | 'NUMBER'
  | 'AUTOPOPULATED'
  | string;

export interface AttributeAuthor {
  id?: number | string;
  firstName?: string;
  lastName?: string;
}

export interface AttributeTemplateRef {
  id?: number | string;
  uuid?: string;
  name?: string;
  [key: string]: unknown;
}

export interface AttributeOption {
  uuid?: string;
  value: string;
  label: string;
  order: number;
  wasRecentlyCreated?: boolean;
}

export interface Attribute {
  id?: number | string;
  uuid: string;
  name: string;
  code: string;
  type: string;
  fieldType: AttributeFieldType;
  dataType?: string;
  isGS1?: boolean;
  modifiable?: boolean;
  description?: string;
  notes?: string;
  order?: number;
  templates?: AttributeTemplateRef[];
  author?: AttributeAuthor | string;
  createdAt?: string;
  updatedAt?: string;
  gs1AiCode?: string;
  gs1AiDefinition?: Gs1AiDefinition | Record<string, unknown>;
  options?: AttributeOption[];
  active?: boolean;
}

export interface AttributesListMeta {
  currentPage: number;
  perPage: number;
  total: number;
  lastPage?: number;
  totalPages?: number;
  to?: number;
}

export interface AttributesListResponse {
  data: Attribute[];
  meta: AttributesListMeta;
}

export interface AttributeSubmitPayload {
  uuid?: string;
  id?: number | string;
  name: string;
  description?: string;
  fieldType: AttributeFieldType;
  dataType?: string;
  modifiable?: boolean;
  options?: AttributeOption[];
  [key: string]: unknown;
}

export interface Gs1AiDefinition {
  aiCode: string;
  aiTitle: string;
  dataFormat?: string;
  category?: string;
  pathSegmentType?: string;
  components?: unknown;
  linters?: unknown;
}

export interface CreateGs1AttributePayload {
  aiCode: string;
  name: string;
  description?: string;
}

export interface ExportAttributesPayload {
  filters: ApiTableFilter[];
  selectedAttributes: string[];
  search?: string;
  allSelected: boolean;
  filterLink?: string;
}

export const DEFAULT_ATTRIBUTES_CURRENT_PAGE = 1;
export const DEFAULT_ATTRIBUTES_PER_PAGE = 25;
export const ATTRIBUTES_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export const ATTRIBUTE_FIELD_TYPES = {
  SELECT: 'DROPDOWN_SELECT',
  TEXT: 'SINGLE_LINE_TEXT',
  DATE: 'DATEPICKER',
  CHECKBOX: 'MULTIPLE_CHECKBOX',
  NUMBER: 'NUMBER_FORMATTED',
  AUTOPOPULATED: 'AUTOPOPULATED',
} as const;

export const ATTRIBUTE_FIELD_TYPE_LABELS: Record<string, string> = {
  DROPDOWN_SELECT: 'Picklist',
  SINGLE_LINE_TEXT: 'Text',
  DATEPICKER: 'Date',
  MULTIPLE_CHECKBOX: 'Checkbox',
  NUMBER_FORMATTED: 'Number',
  AUTOPOPULATED: 'Autopopulated',
  [GS1_ATTRIBUTE_TYPE]: 'GS1',
};

/** Matches React `fieldTypeNames` — unknown / AUTOPOPULATED types display as "System". */
export const ATTRIBUTE_EDITABLE_FIELD_TYPE_LABELS: Record<string, string> = {
  DROPDOWN_SELECT: 'Picklist',
  SINGLE_LINE_TEXT: 'Text',
  DATEPICKER: 'Date',
  MULTIPLE_CHECKBOX: 'Checkbox',
  NUMBER_FORMATTED: 'Number',
};

export const ATTRIBUTE_SYSTEM_TYPES: Record<string, string> = {
  SYSTEM: 'SYSTEM',
  SYSTEM_COMPUTED: 'SYSTEM_COMPUTED',
  SYSTEM_RELATION: 'SYSTEM_RELATION',
  SYSTEM_INTEGRATION: 'SYSTEM_INTEGRATION',
  SYSTEM_OWNED_ATTRIBUTE: 'SYSTEM_OWNED_ATTRIBUTE',
  GS1_APPLICATION_IDENTIFIER: 'GS1_APPLICATION_IDENTIFIER',
};

/** Matches React Type column: system types or non-editable field types show as "System". */
export function isSystemDisplayedAttribute(
  attribute: Pick<Attribute, 'type' | 'fieldType' | 'isGS1' | 'modifiable'> | null | undefined,
): boolean {
  if (!attribute || attribute.isGS1) {
    return false;
  }

  const typeKey = String(attribute.type ?? '').toUpperCase();
  const fieldTypeKey = String(attribute.fieldType ?? '').toUpperCase();

  return (
    attribute.modifiable === false ||
    Boolean(ATTRIBUTE_SYSTEM_TYPES[typeKey]) ||
    fieldTypeKey === ATTRIBUTE_FIELD_TYPES.AUTOPOPULATED ||
    !ATTRIBUTE_EDITABLE_FIELD_TYPE_LABELS[attribute.fieldType]
  );
}

/** GS1 identifier attribute codes. Mirrors React `GS1_IDENTIFIER_CODES`. */
export const GS1_IDENTIFIER_CODES = [
  'gtin', 'itip', 'gmn', 'cpid', 'gln', 'payTo', 'partyGln',
  'gsrnp', 'gsrn', 'gcn', 'sscc', 'gdti', 'ginc', 'gsin', 'grai', 'giai',
] as const;

/** True for GS1 attribute types (legacy or registry-backed). Mirrors React `isGS1Type`. */
export function isGs1AttributeType(type: string | null | undefined): boolean {
  const typeKey = String(type ?? '');

  return (
    typeKey === ATTRIBUTE_SYSTEM_TYPES.SYSTEM_OWNED_ATTRIBUTE ||
    typeKey === ATTRIBUTE_SYSTEM_TYPES.GS1_APPLICATION_IDENTIFIER
  );
}

export const FIELD_TYPES_WITH_OPTIONS = [
  ATTRIBUTE_FIELD_TYPES.SELECT,
  ATTRIBUTE_FIELD_TYPES.CHECKBOX,
] as const;

export const fieldTypesWithOptions = FIELD_TYPES_WITH_OPTIONS;


// =============================================================================
// Attribute Type Filters
// =============================================================================

/** Attribute type for custom/user-defined attributes. */
export const ATTRIBUTE_TYPE_CUSTOM = 'CUSTOM';

/**
 * Check if an attribute is a system attribute.
 * Mirrors React's attributeSystemTypes check.
 */
export function isSystemAttribute(
  attribute: Pick<Attribute, 'type'> | null | undefined,
): boolean {
  if (!attribute) {
    return false;
  }
  const typeKey = String(attribute.type ?? '').toUpperCase();

  return Boolean(ATTRIBUTE_SYSTEM_TYPES[typeKey]);
}

/**
 * Check if an attribute is a custom attribute (type === 'CUSTOM' and not a GS1 identifier).
 * Mirrors React's customAttributesSelector logic.
 */
export function isCustomAttribute(
  attribute: Pick<Attribute, 'code'> & { type?: string | null } | null | undefined,
): boolean {
  if (!attribute) {
    return false;
  }
  const typeKey = String(attribute.type ?? '').toUpperCase();
  const isGS1Identifier = GS1_IDENTIFIER_CODES.includes(attribute.code as typeof GS1_IDENTIFIER_CODES[number]);

  return typeKey === ATTRIBUTE_TYPE_CUSTOM && !isGS1Identifier;
}

/**
 * Check if an attribute is a GS1 attribute.
 * Alias for isGs1AttributeType for consistency with other filters.
 */
export function isGS1Attribute(
  attribute: Pick<Attribute, 'type'> | null | undefined,
): boolean {
  return isGs1AttributeType(attribute?.type);
}
